import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { isOffEnum } from './constants';

/**
 * A department / ticket-type entry field that offers our enum but does not
 * enforce it.
 *
 * A closed `<select>` was the wrong shape. AppDirect's own production data
 * routes 7.2% of tickets to `CNSG Orders`, which is not one of our fifteen
 * departments, and roughly 68% of their real ticket types (`Quote`,
 * `Cable Support Request`, `Commission Inquiry`, …) are outside our eleven —
 * see docs/dispatch/APPDIRECT_TAXONOMY_OBSERVED.md §2-3. A dispatcher reaching
 * for the department their own company actually uses would have hit a wall,
 * and the single most valuable label in the session is exactly the one we
 * could not have recorded.
 *
 * `<input list>` + `<datalist>` rather than a hand-rolled combobox: the known
 * values stay one keystroke away, arbitrary text is accepted, and the popup is
 * the browser's own — so there is no custom listbox competing with the
 * drawer's Escape handler or its focus management on a customer call.
 *
 * The cost of an open field is that a typo becomes a silent off-enum value
 * instead of an error. Hence the marker below: the same condition that
 * legitimately flags `CNSG Orders` also makes `Orderss` visible immediately.
 */
export default function TaxonomyField({
  id,
  label,
  value,
  onChange,
  options,
  disabled,
  aiValue,
  aiEmptyText,
  helpText,
}) {
  const listId = `${id}-options`;
  const offEnum = isOffEnum(value, options);

  return (
    <div>
      <label htmlFor={id} className="block text-xs text-boss-text-secondary mb-1">
        {label}
      </label>
      <input
        id={id}
        type="text"
        list={listId}
        className="boss-input w-full px-3 py-2 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // The field arrives pre-filled with the AI's answer, so the common
        // reason to touch it is to replace that answer, not edit it. Without
        // this, typing inserts at the caret and "Orders" becomes
        // "CNSG OrdersOrders" — which the backend would then store verbatim as
        // ground truth, looking plausible enough to survive review. Selecting
        // on focus makes the first keystroke replace; a second click or an
        // arrow key still gives you a caret if you meant to edit.
        onFocus={(e) => e.target.select()}
        disabled={disabled}
        autoComplete="off"
        // Matches the server's Field(max_length=200); without it an over-long
        // paste returns a 422 the labeller cannot act on.
        maxLength={200}
        placeholder="Pick from the list, or type any value"
        aria-describedby={offEnum ? `${id}-offenum` : undefined}
      />
      <datalist id={listId}>
        {options.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>

      {offEnum && (
        <p
          id={`${id}-offenum`}
          className="text-[11px] mt-1 flex items-start gap-1 text-[var(--boss-warning-text)]"
        >
          <AlertTriangle size={12} className="shrink-0 mt-0.5" />
          <span>
            Not one of our standard values — it will be recorded exactly as typed and counted
            separately, because the AI could not have produced it.
          </span>
        </p>
      )}

      <p className="text-[11px] text-boss-text-muted mt-1">
        AI said: {aiValue || aiEmptyText}
        {helpText ? ` ${helpText}` : ''}
      </p>
    </div>
  );
}
